import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useAuthStore from '../../stores/auth-store.js';
import { useClients } from '../../hooks/useAuth.js';

const ClientContextSelector = () => {
  const isSuperadmin = useAuthStore((state) => state.isSuperadmin);
  const selectedClientId = useAuthStore((state) => state.selectedClientId);
  const setClientContext = useAuthStore((state) => state.setClientContext);
  const clearClientContext = useAuthStore((state) => state.clearClientContext);
  const availableClients = useAuthStore((state) => state.availableClients);
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();

  // Fetch clients (only if Superadmin)
  useClients();

  // Don't render if not Superadmin
  if (!isSuperadmin()) {
    return null;
  }

  const handleClientChange = (e) => {
    const clientId = e.target.value;

    if (clientId === '' || clientId === 'own') {
      // Clear context - view own data
      clearClientContext();
      console.log('✅ Viewing own client data');
    } else {
      // Set context to selected client
      const numericClientId = parseInt(clientId, 10);
      setClientContext(numericClientId);
      console.log(`✅ Switched to client: ${numericClientId}`);
    }

    // Invalidate all evacuation queries to refetch with new context
    queryClient.invalidateQueries({ queryKey: ['evacuation'] });
    
    // Invalidate all settings queries to refetch for new client
    queryClient.invalidateQueries({ queryKey: ['auth', 'settings'] });
    
    // Invalidate all model-mapping queries to refetch with new context
    queryClient.invalidateQueries({ queryKey: ['model-mapping'] });
    
    console.log('🔄 Invalidated evacuation, settings, and model-mapping queries - data will refresh');
  };

  const selectedClient = availableClients.find(
    (client) => client.id === selectedClientId
  );

  return (
    <div className="navbar-item navbar-user dropdown mx-3">
      <div className="d-flex align-items-center">
        <p className="me-2 mb-0">
          Client:
        </p>

        {/* Dropdown Selector */}
        <select
          className="form-select form-select-sm me-2"
          value={selectedClientId || 'own'}
          onChange={handleClientChange}
          style={{ minWidth: '150px' }}
          title="Switch client context to view data for other clients"
        >
          <option value="own">
            {user?.client || 'Own Client'} (Default)
          </option>
          <option disabled>───────────</option>
          {availableClients
            .filter((client) => client.id !== user?.client_id) // Don't show own client twice
            .map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
        </select>

        {/* Badge showing current context */}
        {selectedClient && (
          <span className="badge bg-warning text-dark me-2" title="You are viewing data for this client">
            Acting as: {selectedClient.name}
          </span>
        )}
      </div>
    </div>
  );
};

export default ClientContextSelector;
